import { type ReactNode, useMemo, useRef } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { isAfter, isBefore, isValid, isWithinRange } from 'date-fns'
import { get, isEmpty, isString } from 'lodash'
import { useFormContext, type Validate } from 'react-hook-form'
import { defineMessage, useIntl } from 'react-intl'

import { ScreenReaderBreak, useMergedRefs, useUnique } from '@subaio/components'
import { validateFieldStruct } from '@subaio/components/src/HookForm'
import { define, is, pattern, string } from '@subaio/validation'

import { extractDate } from 'lib/extractDate'
import { useValidation } from 'lib/useValidation'

import { ColumnLabel, IconWrapper, InputError, InputWrapper, Label, Placeholder } from '../Shared'
import { InputComponent } from './InputComponent'
import { Textarea } from './Textarea'

type Props = {
	label: ReactNode
	icon?: ReactNode
	name: string

	autoComplete?: string
	isDisabled?: boolean
	isReadOnly?: boolean
	isRequired?: boolean
	/** Max setting for a date input */
	maxDate?: string
	maxLength?: number
	maxRows?: number
	/** Min setting for a date input */
	minDate?: string
	minLength?: number
	minRows?: number
	placeholder?: string
	placeholderAriaLabel?: string
	shouldAutofocus?: boolean
	showOptionalHint?: boolean
	validate?: Validate<any, any>
	type?: 'email' | 'hidden' | 'number' | 'password' | 'tel' | 'text' | 'username' | 'multiline' | 'date-written'
}

const isValidDate = (value: string) => is(value, pattern(string(), /^\d{2}-?\d{2}-?\d{4}$/))

export const Input = ({
	isRequired = false,
	type = 'text',
	isDisabled = false,
	minLength,
	maxLength,
	name,
	autoComplete = 'off',
	isReadOnly = false,
	label,
	icon,
	placeholder,
	shouldAutofocus,
	validate,
	minRows = 3,
	maxRows = 8,
	minDate,
	maxDate,
	placeholderAriaLabel,
}: Props) => {
	const id = useUnique(name)
	const {
		register,
		formState: { errors },
		watch,
	} = useFormContext()
	const { isFocused, focusProps } = useFocusRing({ isTextInput: true, autoFocus: shouldAutofocus })
	const { hoverProps, isHovered } = useHover({})
	const inputRef = useRef<HTMLInputElement>(null)
	const { formatMessage, formatDate } = useIntl()
	const { fieldEmailOptional, fieldNumberOptional, fieldStringOptional } = useValidation()
	const fieldValue = watch(name)

	const inputMode = useMemo(() => {
		switch (type) {
			case 'date-written':
				return 'numeric'
			case 'email':
				return 'email'
			case 'hidden':
				return 'none'
			case 'number':
				return 'numeric'
			case 'tel':
				return 'tel'
			default:
				return 'text'
		}
	}, [type])
	const inputType = useMemo(() => {
		switch (type) {
			case 'number':
				return 'text'
			case 'date-written':
				return 'text'
			default:
				return type
		}
	}, [type])

	const inputProps = register(name, {
		required: isRequired
			? formatMessage(defineMessage({ id: 'formFields.input.isRequired', defaultMessage: 'This field is required' }))
			: false,
		disabled: isDisabled,
		validate: {
			email: (value) => (type === 'email' ? validateFieldStruct(value, fieldEmailOptional()) : true),
			number: (value) => (type === 'number' ? validateFieldStruct(value, fieldNumberOptional()) : true),
			'date-written': (value) =>
				type === 'date-written'
					? validateFieldStruct(
							value,
							define<string>('fieldDate', (value) => {
								const isValidString = is(value, fieldStringOptional())
								if (!isValidString) {
									return formatMessage(
										defineMessage({
											id: 'formFields.input.dateInvalidString',
											defaultMessage: 'Enter a valid date',
										}),
									)
								}
								if (value) {
									const isValidDate = is(value, pattern(string(), /(\d{2})-?(\d{2})-?(\d{4})/))
									if (!isValidDate) {
										return formatMessage(
											defineMessage({
												id: 'formFields.input.dateInvalidString',
												defaultMessage: 'Enter a valid date',
											}),
										)
									}
									const [, day, month, year] = /(\d{2})-?(\d{2})-?(\d{4})/.exec(value)!
									const date = new Date(`${year}-${month}-${day}`)
									// If the returned date cannot be parsed as valid
									if (!isValid(date)) {
										return formatMessage(
											defineMessage({
												id: 'formFields.input.dateInvalidString',
												defaultMessage: 'Enter a valid date',
											}),
										)
									}

									// If the date is not within the minimum range
									if (minDate && !maxDate && isAfter(minDate, date)) {
										return formatMessage(
											defineMessage({
												id: 'formFields.input.dateInvalidString',
												defaultMessage: 'Enter a valid date',
											}),
										)
									}
									// If the date is not within the maximum range
									if (maxDate && !minDate && isBefore(maxDate, date)) {
										return formatMessage(
											defineMessage({
												id: 'formFields.input.dateInvalidString',
												defaultMessage: 'Enter a valid date',
											}),
										)
									}
									// If the date is not within the minimum and maximum range
									if (maxDate && minDate && !isWithinRange(date, minDate, maxDate)) {
										return formatMessage(
											defineMessage({
												id: 'formFields.input.dateInvalidString',
												defaultMessage: 'Enter a valid date',
											}),
										)
									}
								}
								return true
							}),
						)
					: true,
			customValidation: validate ?? (() => true),
		},
		setValueAs: (value: unknown) => {
			if (type === 'number') {
				if (isEmpty(value) || Number.isNaN(value)) return undefined
				return isString(value) && !isEmpty(value.trim()) ? Number(value) : undefined
			}
			return isString(value) && isEmpty(value) ? undefined : value
		},
	})
	const sharedRef = useMergedRefs<HTMLInputElement | HTMLTextAreaElement>(inputProps.ref, inputRef)

	const error = get(errors, name)
	const hasError = Boolean(error)
	const hasContent = Boolean(inputRef.current?.value)
	const hasIcon = Boolean(icon)
	const defaultProps = {
		...focusProps,
		...hoverProps,
		// Avoid typing issue with react-autosize-textarea
		style: undefined,
		'aria-disabled': isReadOnly || isDisabled,
		'aria-required': isRequired,
		'aria-invalid': hasError,
		'aria-labelledby': `${id}-label`,
		'aria-describedby': `${id}-error`,
		autoFocus: shouldAutofocus,
		type: inputType,
		// Needs to be passed as both `readOnly` (for HTML) and `isReadOnly` (for styling)
		readOnly: isReadOnly,
		ref: sharedRef,
		autoComplete,
		hasError,
		id,
		inputMode,
		isDisabled,
		isFocused,
		isHovered,
		isReadOnly,
		maxLength,
		minLength,
	} as const

	return (
		<ColumnLabel>
			{label && (
				<Label
					id={`${id}-label`}
					htmlFor={id}
					hasError={hasError}
					hasIcon={hasIcon}
					isFocused={isFocused && !isReadOnly && !isDisabled}
					isDisabled={isDisabled}
					aria-hidden
				>
					{label}
					<ScreenReaderBreak />
				</Label>
			)}
			<InputWrapper>
				{icon && (
					<IconWrapper hasError={hasError} isVisible={!hasContent && !isFocused} aria-hidden>
						{icon}
					</IconWrapper>
				)}
				{placeholder && (
					<Placeholder
						hasError={hasError}
						hasIcon={hasIcon}
						isVisible={!hasContent && !isFocused}
						aria-label={placeholderAriaLabel}
					>
						{placeholder}
					</Placeholder>
				)}
				{type === 'multiline' ? (
					<Textarea {...inputProps} {...defaultProps} minRows={minRows} maxRows={maxRows} />
				) : (
					<InputComponent
						{...inputProps}
						{...defaultProps}
						aria-valuetext={
							type === 'date-written' && isValidDate(fieldValue)
								? formatDate(extractDate(fieldValue), { day: 'numeric', month: 'long', year: 'numeric' })
								: undefined
						}
					/>
				)}
			</InputWrapper>
			<InputError id={`${id}-error`} hasError={hasError} errorMessage={error?.message as string | undefined} />
		</ColumnLabel>
	)
}
